/**
 * DB Hygiene Cleanup — Session 81 follow-up.
 *
 * Lands here when the operator clicks the "Open Clean-up Page" button
 * in the nightly DB hygiene email. Re-scans live (so the deletion
 * list is current, not the stale snapshot from the email render)
 * and gates the actual delete behind an explicit "Confirm cleanup"
 * click.
 *
 * Auth: platform_admin only. Anyone else gets bounced by the
 * authenticated `/api/admin/db-hygiene/cleanup` endpoint.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Trash2, RefreshCw, ArrowLeft, ShieldAlert } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

export default function DbHygieneCleanupPage() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const loadPreview = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.post("/admin/db-hygiene/cleanup");  // confirm=false default → preview
      setPreview(res.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Failed to load preview";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPreview(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const onConfirm = async () => {
    if (!window.confirm(
      `This will delete ${preview?.total_items || 0} suspect rows from the live database. ` +
      "Are you sure?"
    )) return;
    setConfirming(true);
    try {
      const res = await api.post("/admin/db-hygiene/cleanup?confirm=true");
      setResult(res.data);
      toast.success(`Cleaned up ${res.data.total} row(s).`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Cleanup failed";
      toast.error(msg);
      setError(msg);
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-8" data-testid="db-hygiene-cleanup-page">
      <Link to="/admin" className="text-[12px] text-[#0055FF] hover:underline inline-flex items-center gap-1 mb-4" data-testid="back-link">
        <ArrowLeft size={12} /> Admin home
      </Link>
      <h1 className="text-2xl font-semibold text-[#0A0A0A] mb-2">DB hygiene clean-up</h1>
      <p className="text-[13px] text-[#525252] mb-6">
        This page deletes the test-pattern rows surfaced by the nightly hygiene
        scanner. We re-scan live the moment you open this page (the list
        below is current — NOT the snapshot from the email), and every row is
        re-validated for foreign-key safety before deletion. Audit-log
        actor: <strong>you</strong>.
      </p>

      {loading && (
        <div className="border border-[#E5E5E5] p-6 text-center text-[13px] text-[#525252]" data-testid="loading-state">
          <RefreshCw className="inline-block animate-spin mr-2" size={14} />
          Re-scanning live database…
        </div>
      )}

      {error && (
        <div className="border border-[#DC2626] bg-[#FEF2F2] p-4 text-[13px] text-[#DC2626] mb-4" data-testid="error-banner">
          <ShieldAlert size={14} className="inline-block mr-1" /> {error}
        </div>
      )}

      {!loading && preview && !result && (
        <div data-testid="preview-block">
          {preview.total_items === 0 ? (
            <div className="border border-[#BBF7D0] bg-[#F0FDF4] p-6 text-center text-[14px] text-[#16A34A]" data-testid="clean-banner">
              ✓ Database is clean — nothing to remove.
            </div>
          ) : (
            <>
              <div className="border border-[#FCD34D] bg-[#FFFBEB] p-4 mb-5">
                <p className="text-[13px] text-[#92400E] font-semibold mb-2">
                  {preview.total_items} suspect row(s) will be deleted:
                </p>
                <table className="w-full text-[13px]">
                  <tbody>
                    {Object.entries(preview.counts).filter(([_k, n]) => n > 0).map(([k, n]) => (
                      <tr key={k} data-testid={`preview-row-${k}`}>
                        <td className="py-1 text-[#525252] capitalize">{k.replace(/_/g, " ")}</td>
                        <td className="py-1 text-right font-mono font-bold text-[#0A0A0A]">{n}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  onClick={onConfirm}
                  disabled={confirming}
                  className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                  data-testid="confirm-cleanup-btn"
                >
                  <Trash2 size={14} className="mr-1.5" />
                  {confirming ? "Cleaning up…" : `Confirm cleanup of ${preview.total_items} row(s)`}
                </Button>
                <Button
                  onClick={loadPreview}
                  variant="outline"
                  disabled={confirming}
                  data-testid="rescan-btn"
                >
                  <RefreshCw size={14} className="mr-1.5" /> Re-scan
                </Button>
                <Button
                  onClick={() => navigate("/admin")}
                  variant="ghost"
                  disabled={confirming}
                  data-testid="cancel-btn"
                >
                  Cancel
                </Button>
              </div>
              <p className="text-[11px] text-[#A3A3A3] mt-3">
                Each row is double-checked for live foreign-key references before
                deletion. Rows that have gained real attachments since the email
                render are skipped (you'll see them again on tomorrow's digest only
                if they still match a pattern AND remain orphaned).
              </p>
            </>
          )}
        </div>
      )}

      {result && (
        <div className="border border-[#BBF7D0] bg-[#F0FDF4] p-6" data-testid="result-block">
          <h2 className="text-[15px] font-semibold text-[#16A34A] mb-3">
            ✓ Cleaned up {result.total} row(s).
          </h2>
          <table className="w-full text-[13px] mb-4">
            <tbody>
              {Object.entries(result.summary).filter(([_k, n]) => n > 0).map(([k, n]) => (
                <tr key={k}>
                  <td className="py-1 text-[#525252] capitalize">{k.replace(/_/g, " ")}</td>
                  <td className="py-1 text-right font-mono font-bold text-[#16A34A]">{n}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[11px] text-[#525252]">
            Audit-logged as <code>db_hygiene_cleanup</code>. Run the
            nightly scan again to confirm the database is clean.
          </p>
          <Button
            onClick={() => { setResult(null); loadPreview(); }}
            variant="outline"
            className="mt-3"
            data-testid="rescan-after-result-btn"
          >
            <RefreshCw size={14} className="mr-1.5" /> Re-scan now
          </Button>
        </div>
      )}
    </div>
  );
}
